import type { DeviceClass } from '../engine/particles/particleBudget.js'

const MOBILE_QUERY = '(max-width: 800px)'

export interface DeviceClassSignals {
  readonly hardwareConcurrency: number | null
  readonly maxWidth800Matches: boolean
}

export interface BrowserDeviceClassEnvironment {
  readonly navigator?: unknown
  readonly window?: unknown
}

function currentBrowserEnvironment(): BrowserDeviceClassEnvironment {
  let browserNavigator: unknown
  let browserWindow: unknown
  try {
    browserNavigator = typeof navigator === 'undefined' ? undefined : navigator
  } catch {
    browserNavigator = undefined
  }
  try {
    browserWindow = typeof window === 'undefined' ? undefined : window
  } catch {
    browserWindow = undefined
  }
  return { navigator: browserNavigator, window: browserWindow }
}

/** Capture optional browser signals without allowing host accessors to break startup. */
export function readBrowserDeviceClassSignals(
  environment: BrowserDeviceClassEnvironment = currentBrowserEnvironment(),
): DeviceClassSignals {
  let hardwareConcurrency: number | null = null
  let maxWidth800Matches = false
  try {
    const browserNavigator = environment.navigator
    if (typeof browserNavigator === 'object' && browserNavigator !== null) {
      const candidate = (browserNavigator as { readonly hardwareConcurrency?: unknown }).hardwareConcurrency
      if (typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate > 0) {
        hardwareConcurrency = candidate
      }
    }
  } catch {
    hardwareConcurrency = null
  }
  try {
    const browserWindow = environment.window
    if (typeof browserWindow === 'object' && browserWindow !== null) {
      const matchMedia = (browserWindow as { readonly matchMedia?: unknown }).matchMedia
      if (typeof matchMedia === 'function') {
        const result: unknown = Reflect.apply(matchMedia, browserWindow, [MOBILE_QUERY])
        if (typeof result === 'object' && result !== null) {
          maxWidth800Matches = (result as { readonly matches?: unknown }).matches === true
        }
      }
    }
  } catch {
    maxWidth800Matches = false
  }
  return { hardwareConcurrency, maxWidth800Matches }
}

/** Pure canonical policy: constrained hardware wins, then narrow viewport, then desktop. */
export function detectDeviceClass(signals: DeviceClassSignals): DeviceClass {
  if (signals.hardwareConcurrency !== null && signals.hardwareConcurrency <= 4) {
    return 'constrained'
  }
  return signals.maxWidth800Matches ? 'mobile' : 'desktop'
}
